"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Place, PlaceCategory } from "@/lib/places";
import { buildPlaceSlugMap, placeDistrict, districtSlug, tagSlug, tagLabel, resolveCategoryLabel, rankScore, getUniqueDistricts, getUniqueTags } from "@/lib/places";

export default function PlacesExplorer({
  places,
  categories,
  cities,
}: {
  places: Place[];
  categories: PlaceCategory[];
  cities: string[];
}) {
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("All");
  const [category, setCategory] = useState("All");
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const districts = useMemo(() => getUniqueDistricts(places), [places]);
  const slugMap = useMemo(() => buildPlaceSlugMap(places), [places]);
  const topTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of places) if (p.tags) for (const t of p.tags) counts.set(t, (counts.get(t) || 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([t]) => t);
  }, [places]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return places
      .filter((p) => {
        const matchesCity = city === "All" || p.city === city || placeDistrict(p) === city;
        const matchesCategory = category === "All" || resolveCategoryLabel(p.category, categories) === category;
        const matchesTag = !activeTag || (p.tags?.includes(activeTag) ?? false);
        const matchesQuery =
          !q ||
          p.name.toLowerCase().includes(q) ||
          p.city.toLowerCase().includes(q) ||
          (p.district || "").toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q);
        return matchesCity && matchesCategory && matchesTag && matchesQuery;
      })
      .sort((a, b) => rankScore(b) - rankScore(a));
  }, [places, query, city, category, activeTag, categories]);

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 focus:border-cyan-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
          >
            <option value="All">All Districts</option>
            {districts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 focus:border-cyan-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-300"
          >
            <option value="All">All Categories</option>
            {categories.map((c) => (
              <option key={c.categoryId} value={c.label}>
                {c.icon} {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="relative w-full sm:w-64">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search places..."
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-700 focus:border-cyan-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
          />
        </div>
      </div>

      {topTags.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {topTags.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTag(activeTag === t ? null : t)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                activeTag === t
                  ? "bg-cyan-500 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              {tagLabel(t)}
            </button>
          ))}
        </div>
      ) : null}

      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
        {filtered.length} place{filtered.length === 1 ? "" : "s"}
        {city !== "All" ? ` in ${city}` : ""}
        {category !== "All" ? ` · ${category}` : ""}
        {activeTag ? ` · ${tagLabel(activeTag)}` : ""}
      </p>

      {filtered.length ? (
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <Link
              key={p.placeId}
              href={`/places/${slugMap.get(p.placeId) ?? p.placeId}`}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-[#0f172a]/80"
            >
              <div className="relative h-48 w-full bg-slate-100 dark:bg-slate-800">
                {p.heroPhotoUrl ? (
                  <Image
                    src={p.heroPhotoUrl}
                    alt={`${p.name} in ${p.city}`}
                    fill
                    className="object-cover"
                    unoptimized
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-4xl">🗺️</div>
                )}
                {p.isTrending ? (
                  <span className="absolute left-2 top-2 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-semibold text-white">
                    🔥 Trending
                  </span>
                ) : null}
                {p.rating ? (
                  <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                    ★ {p.rating.toFixed(1)}
                  </span>
                ) : null}
              </div>
              <div className="p-4">
                <p className="truncate font-semibold text-slate-900 dark:text-white">{p.name}</p>
                <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                  {p.city}
                  {p.category ? ` · ${resolveCategoryLabel(p.category, categories)}` : ""}
                </p>
                {p.description ? (
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{p.description}</p>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-10 text-center text-sm text-slate-500 dark:text-slate-400">
          No places match your filters. Try a different city or category.
        </p>
      )}
    </div>
  );
}
